import { useEffect, useState } from "react";
import { Box, Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useGo } from "./ConsoleShell";
import { VfToggle } from "./primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useQueryStore } from "../stores/queryStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { isValidFriendlyName, stripWorkspacePrefix } from "../lib/namespace";
import css from "./modals.module.css";

/** All dialog-style overlays. `ui.modal` selects which one is mounted. */
export function ConsoleModals() {
  const modal = useUiStore((s) => s.modal);
  const close = useUiStore((s) => s.closeModal);

  return (
    <>
      <Shell opened={modal === "createKey"} onClose={close} title="create api key" width={480}>
        <CreateKeyForm onDone={close} />
      </Shell>
      <Shell opened={modal === "connectRepo"} onClose={close} title="connect repository" width={520}>
        <ConnectRepoForm onDone={close} />
      </Shell>
      <Shell opened={modal === "invite"} onClose={close} title="invite member" width={480}>
        <InviteForm onDone={close} />
      </Shell>
      <Shell opened={modal === "newWorkspace"} onClose={close} title="new workspace" width={480}>
        <NewWorkspaceForm onDone={close} />
      </Shell>
      <Shell opened={modal === "upsertDoc"} onClose={close} title="upsert document" width={620}>
        <UpsertDocForm onDone={close} />
      </Shell>
      <Shell opened={modal === "renameNamespace"} onClose={close} title="rename namespace" width={480}>
        <RenameNamespaceForm onDone={close} />
      </Shell>
      <Shell opened={modal === "deleteNamespace"} onClose={close} title="delete namespace" width={520}>
        <DeleteNamespaceForm onDone={close} />
      </Shell>
      <Shell opened={modal === "workspaceMenu"} onClose={close} title="switch workspace" width={440}>
        <WorkspaceMenu onDone={close} />
      </Shell>
      <Shell opened={modal === "renameGeneric"} onClose={close} title="rename" width={460}>
        <RenameGenericForm onDone={close} />
      </Shell>
    </>
  );
}

function Shell({
  opened,
  onClose,
  title,
  width,
  children,
}: {
  opened: boolean;
  onClose: () => void;
  title: string;
  width: number;
  children: React.ReactNode;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={width}
      withCloseButton={false}
      centered
      overlayProps={{ backgroundOpacity: 0.6, color: "#050503" }}
      classNames={{ content: css.modal, body: css.body }}
    >
      <div className={css.head}>
        <span className={css.title}>{title}</span>
        <button type="button" className={css.close} onClick={onClose} aria-label="close">
          [esc]
        </button>
      </div>
      <div className={css.inner}>{children}</div>
    </Modal>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className={css.label}>{children}</div>;
}

const inputStyles = {
  input: {
    background: "var(--vf-bg)",
    borderColor: "var(--vf-border)",
    color: "var(--vf-acc)",
    fontSize: "13.5px",
  },
} as const;

// ------------------------------------------------------------ create key ----

// The wire model is a single `access` level plus an optional collection list
// (createKeyRequest in workspace_keys.go); the console shows it as scope chips.
const ACCESS_LEVELS: { value: "read" | "read_write"; label: string }[] = [
  { value: "read_write", label: "docs:read + docs:write" },
  { value: "read", label: "docs:read" },
];

function CreateKeyForm({ onDone }: { onDone: () => void }) {
  const go = useGo();
  const createKey = useConsoleStore((s) => s.createKey);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const loadNamespaces = useConsoleStore((s) => s.loadNamespaces);
  const workspaceId = useWorkspaceStore((s) => s.currentId);

  // The "scoped to" list comes from the namespaces slice, which nothing on the
  // keys route populates. Landing on /keys directly — the normal path for
  // "mint a key for the collection I just made" — otherwise offers only
  // whole-workspace scope, silently making the key broader than intended.
  // Depends on workspaceId: opening the modal before workspace discovery has
  // finished would otherwise fetch with no workspace, fail, and never retry.
  useEffect(() => {
    if (workspaceId && namespaces.length === 0) void loadNamespaces();
  }, [workspaceId, namespaces.length, loadNamespaces]);
  const [name, setName] = useState("");
  const [access, setAccess] = useState<"read" | "read_write">("read_write");
  const [collection, setCollection] = useState("");
  const [busy, setBusy] = useState(false);

  const grant = collection ? `${workspaceId}/${collection}` : `${workspaceId}/*`;

  return (
    <>
      <Label>// key name</Label>
      <TextInput
        value={name}
        placeholder="production-agent"
        mb={16}
        styles={inputStyles}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <Label>// capability scopes</Label>
      <select
        className={css.select}
        style={{ marginBottom: 16 }}
        value={access}
        aria-label="capability scopes"
        onChange={(e) => setAccess(e.currentTarget.value as "read" | "read_write")}
      >
        {ACCESS_LEVELS.map((a) => (
          <option key={a.value} value={a.value}>
            {a.label}
          </option>
        ))}
      </select>

      <Label>// scoped to</Label>
      <select
        className={css.select}
        value={collection}
        aria-label="scoped to"
        onChange={(e) => setCollection(e.currentTarget.value)}
      >
        <option value="">the whole workspace</option>
        {/* Value is the friendly name derived from the namespace KEY, not the
            display label: PATCH sets a label independently of the key, so after
            a rename the label no longer identifies the namespace and the grant
            would point at something that does not exist. Label is for reading,
            key is for authorizing. */}
        {namespaces.map((n) => {
          const friendly = workspaceId ? stripWorkspacePrefix(n.id, workspaceId) : null;
          if (!friendly) return null;
          return (
            <option key={n.id} value={friendly}>
              {n.label}
            </option>
          );
        })}
      </select>

      <Text fz="12px" c="var(--vf-dim)" mt={16} lh={1.6}>
        // grant pattern <span style={{ color: "var(--vf-ok)" }}>{grant}</span> — the secret is
        shown once, immediately after creation.
      </Text>

      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void createKey({ name, access, collections: collection ? [collection] : [] })
              .then(() => {
                onDone();
                go("/keys");
              })
              .catch((e: unknown) =>
                notifications.show({
                  message: e instanceof Error ? e.message : String(e),
                  color: "red",
                }),
              )
              .finally(() => setBusy(false));
          }}
        >
          ▸ create key
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// ---------------------------------------------------------- connect repo ----

function ConnectRepoForm({ onDone }: { onDone: () => void }) {
  const go = useGo();
  const connectRepo = useConsoleStore((s) => s.connectRepo);
  const [fullName, setFullName] = useState("");
  const [branch, setBranch] = useState("main");
  const [webhook, setWebhook] = useState(true);
  const [busy, setBusy] = useState(false);

  const valid = /^[\w.-]+\/[\w.-]+$/.test(fullName.trim());

  return (
    <>
      <Label>// repository (owner/name)</Label>
      <TextInput
        value={fullName}
        placeholder="acme/platform"
        mb={16}
        styles={inputStyles}
        onChange={(e) => setFullName(e.currentTarget.value)}
      />

      <Label>// default branch</Label>
      <TextInput
        value={branch}
        mb={16}
        styles={inputStyles}
        onChange={(e) => setBranch(e.currentTarget.value)}
      />

      <Group justify="space-between" mb={6}>
        <Box>
          <Text fz="13px" c="var(--vf-text)">
            register webhook
          </Text>
          <Text fz="11.5px" c="var(--vf-dim2)" mt={3}>
            push → incremental reindex, instead of a nightly full rebuild
          </Text>
        </Box>
        <VfToggle checked={webhook} onChange={setWebhook} aria-label="register webhook" />
      </Group>

      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={!valid || busy}
          onClick={() => {
            setBusy(true);
            void connectRepo({ fullName: fullName.trim(), defaultBranch: branch.trim(), webhook })
              .then(() => {
                notifications.show({ message: `connected ${fullName.trim()}`, color: "green" });
                onDone();
                go("/repos");
              })
              .catch((e: unknown) => notifications.show({ message: String(e), color: "red" }))
              .finally(() => setBusy(false));
          }}
        >
          ▸ connect repo
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// ----------------------------------------------------------------- invite ---

function InviteForm({ onDone }: { onDone: () => void }) {
  const invite = useWorkspaceStore((s) => s.invite);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const valid = /@/.test(email);

  return (
    <>
      <Label>// email</Label>
      <TextInput
        value={email}
        placeholder="teammate@acme.ai"
        mb={16}
        styles={inputStyles}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />

      <Label>// role</Label>
      <select
        className={css.select}
        value={role}
        aria-label="role"
        onChange={(e) => setRole(e.currentTarget.value)}
      >
        <option value="member">member</option>
        <option value="admin">admin</option>
        <option value="service">service</option>
      </select>

      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={!valid}
          onClick={() => {
            void invite({ email, role }).then(() => {
              notifications.show({ message: `invited ${email}`, color: "green" });
              onDone();
            });
          }}
        >
          ▸ send invite
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// -------------------------------------------------------- new workspace -----

const REGIONS = ["us-central1", "us-east1", "europe-west1", "asia-southeast1"];

function NewWorkspaceForm({ onDone }: { onDone: () => void }) {
  const go = useGo();
  const create = useWorkspaceStore((s) => s.create);
  const profiles = useConsoleStore((s) => s.profiles);
  const resetConsole = useConsoleStore((s) => s.resetAll);
  const resetQuery = useQueryStore((s) => s.reset);
  const [name, setName] = useState("");
  const [region, setRegion] = useState(REGIONS[0]);
  const [embedder, setEmbedder] = useState("nomic-text-v1.5");

  return (
    <>
      <Label>// workspace name</Label>
      <TextInput
        value={name}
        placeholder="acme-ai"
        mb={16}
        styles={inputStyles}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <Label>// region</Label>
      <select
        className={css.select}
        style={{ marginBottom: 16 }}
        value={region}
        aria-label="region"
        onChange={(e) => setRegion(e.currentTarget.value)}
      >
        {REGIONS.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>

      <Label>// default embedder</Label>
      <select
        className={css.select}
        value={embedder}
        aria-label="default embedder"
        onChange={(e) => setEmbedder(e.currentTarget.value)}
      >
        {(profiles.length ? profiles.map((p) => p.name) : [embedder]).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>

      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={name.trim().length === 0}
          onClick={() => {
            void create({ name, region, embedder }).then(() => {
              // Creating switches into the new workspace, so the cached rows and
              // query results of the previous one have to go — same as the
              // switcher in WorkspaceMenu.
              resetConsole();
              resetQuery();
              notifications.show({ message: `created workspace ${name}`, color: "green" });
              onDone();
              go("/dashboard");
            });
          }}
        >
          ▸ create workspace
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// ----------------------------------------------------------- upsert doc -----

function UpsertDocForm({ onDone }: { onDone: () => void }) {
  const namespaces = useConsoleStore((s) => s.namespaces);
  const documentsNamespace = useConsoleStore((s) => s.documentsNamespace);
  const upsertDocument = useConsoleStore((s) => s.upsertDocument);

  const [namespace, setNamespace] = useState(documentsNamespace ?? namespaces[0]?.id ?? "");
  const [id, setId] = useState("");
  const [content, setContent] = useState("");
  const [embed, setEmbed] = useState<"server" | "client">("server");
  const [vectorText, setVectorText] = useState("");
  const [metaKey, setMetaKey] = useState("category");
  const [metaValue, setMetaValue] = useState("");

  useEffect(() => {
    if (documentsNamespace) setNamespace(documentsNamespace);
  }, [documentsNamespace]);

  const dims = namespaces.find((n) => n.id === namespace)?.dims ?? 0;
  const vector = vectorText
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);
  const vectorOk = vector.length > 0 && vector.every((v) => Number.isFinite(v));
  // The server rejects a mismatched vector, so block it here rather than
  // surfacing a 400 after the fact.
  const vectorFits = !dims || vector.length === dims;

  const valid =
    content.trim().length > 0 && (embed === "server" || (vectorOk && vectorFits));

  return (
    <>
      <Label>// namespace</Label>
      <select
        className={css.select}
        style={{ marginBottom: 16 }}
        value={namespace}
        aria-label="namespace"
        onChange={(e) => setNamespace(e.currentTarget.value)}
      >
        {namespaces.map((n) => (
          <option key={n.id} value={n.id}>
            {n.label}
          </option>
        ))}
      </select>

      <Label>// document id (optional — generated when blank)</Label>
      <TextInput
        value={id}
        placeholder="kb_8842"
        mb={16}
        styles={inputStyles}
        onChange={(e) => setId(e.currentTarget.value)}
      />

      <Label>// content</Label>
      <textarea
        className={css.textarea}
        rows={5}
        value={content}
        aria-label="document content"
        onChange={(e) => setContent(e.currentTarget.value)}
      />

      <Box mt={16} mb={16}>
        <Label>// embedding</Label>
        <select
          className={css.select}
          style={{ width: 260 }}
          value={embed}
          aria-label="embedding mode"
          onChange={(e) => setEmbed(e.currentTarget.value as "server" | "client")}
        >
          <option value="server">server-side (from content)</option>
          <option value="client">client-supplied vector</option>
        </select>

        {embed === "client" ? (
          <Box mt={12}>
            <Label>
              // vector{" "}
              <span style={{ color: "var(--vf-dim2)" }}>
                ({dims ? `${dims} floats` : "comma or space separated"})
              </span>
            </Label>
            <textarea
              className={css.textarea}
              rows={3}
              value={vectorText}
              aria-label="vector"
              placeholder="0.021, -0.114, 0.307, …"
              onChange={(e) => setVectorText(e.currentTarget.value)}
            />
            {vectorText && !vectorOk ? (
              <Text fz="11.5px" c="var(--vf-bad)" mt={6}>
                ✗ could not parse those as numbers
              </Text>
            ) : null}
            {vectorOk && !vectorFits ? (
              <Text fz="11.5px" c="var(--vf-bad)" mt={6}>
                ✗ {vector.length} values — this namespace expects {dims}
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Label>// metadata</Label>
      <Group gap={10} wrap="nowrap">
        <TextInput
          value={metaKey}
          placeholder="key"
          style={{ flex: 1 }}
          styles={inputStyles}
          onChange={(e) => setMetaKey(e.currentTarget.value)}
        />
        <TextInput
          value={metaValue}
          placeholder="value"
          style={{ flex: 1 }}
          styles={inputStyles}
          onChange={(e) => setMetaValue(e.currentTarget.value)}
        />
      </Group>

      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={!valid || !namespace}
          onClick={() => {
            void upsertDocument(namespace, {
              id,
              content,
              embed,
              ...(embed === "client" ? { vector } : {}),
              metadata: metaKey.trim() ? { [metaKey.trim()]: metaValue } : {},
            })
              .then(() => {
                notifications.show({ message: "202 accepted — queued for ingest" });
                onDone();
              })
              .catch((e: unknown) =>
                notifications.show({
                  message: e instanceof Error ? e.message : String(e),
                  color: "red",
                }),
              );
          }}
        >
          ▸ upsert document
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// ------------------------------------------------------ rename namespace ----

function RenameNamespaceForm({ onDone }: { onDone: () => void }) {
  const detail = useUiStore((s) => s.detail);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const renameNamespace = useConsoleStore((s) => s.renameNamespace);
  const target = namespaces.find((n) => n.id === detail?.id) ?? namespaces[0];
  const [value, setValue] = useState(target?.label ?? "");

  const valid = value.trim().length > 0 && isValidFriendlyName(value.trim());

  if (!target) return null;

  return (
    <>
      <Text fz="12.5px" c="var(--vf-sec)" lh={1.7} mb={16}>
        Rename the display label for <span style={{ color: "var(--vf-acc)" }}>{target.id}</span>. The
        namespace key itself is immutable — documents, grants and API paths are unaffected.
      </Text>
      <Label>// new name</Label>
      <TextInput
        value={value}
        styles={inputStyles}
        onChange={(e) => setValue(e.currentTarget.value)}
      />
      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={!valid}
          onClick={() => {
            void renameNamespace(target.id, value.trim()).then(() => {
              notifications.show({ message: `renamed to ${value.trim()}` });
              onDone();
            });
          }}
        >
          ▸ rename
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// ------------------------------------------------------ delete namespace ----

function DeleteNamespaceForm({ onDone }: { onDone: () => void }) {
  const go = useGo();
  const detail = useUiStore((s) => s.detail);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const deleteNamespace = useConsoleStore((s) => s.deleteNamespace);
  const target = namespaces.find((n) => n.id === detail?.id);
  const [typed, setTyped] = useState("");

  if (!target) return null;
  // Type-to-arm: the destructive button stays inert until the name matches.
  const armed = typed.trim() === target.label;

  return (
    <>
      <Text fz="12.5px" c="var(--vf-bad)" mb={10}>
        $ vf namespaces delete --force
      </Text>
      <Text fz="12.5px" c="var(--vf-sec)" lh={1.7} mb={16}>
        Delete {target.label} and its {target.vectors.toLocaleString()} vectors. The namespace moves
        to trash with a 7-day grace period before the blobs are reaped.
      </Text>
      <Label>
        // type <span style={{ color: "var(--vf-acc)" }}>{target.label}</span> to confirm
      </Label>
      <TextInput
        value={typed}
        styles={inputStyles}
        onChange={(e) => setTyped(e.currentTarget.value)}
      />
      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          color="red"
          variant={armed ? "filled" : "light"}
          disabled={!armed}
          onClick={() => {
            void deleteNamespace(target.id).then(() => {
              notifications.show({ message: `deleted ${target.label}`, color: "red" });
              onDone();
              go("/namespaces");
            });
          }}
        >
          delete namespace
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}

// -------------------------------------------------------- workspace menu ----

function WorkspaceMenu({ onDone }: { onDone: () => void }) {
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const currentId = useWorkspaceStore((s) => s.currentId);
  const select = useWorkspaceStore((s) => s.select);
  const resetConsole = useConsoleStore((s) => s.resetAll);
  const resetQuery = useQueryStore((s) => s.reset);
  const openModal = useUiStore((s) => s.openModal);

  return (
    <>
      <Stack gap={2} mb={16}>
        {workspaces.map((w) => (
          <button
            key={w.id}
            type="button"
            className={`${css.wsRow} ${w.id === currentId ? css.wsRowActive : ""}`}
            onClick={() => {
              if (w.id !== currentId) {
                select(w.id);
                // Cached rows and query results belong to the previous tenant.
                resetConsole();
                resetQuery();
              }
              onDone();
            }}
          >
            <span>{w.id === currentId ? "▸" : "·"}</span>
            <span style={{ flex: 1 }}>{w.name}</span>
            {w.role ? <span className={css.wsRole}>{w.role}</span> : null}
          </button>
        ))}
      </Stack>
      <Button fullWidth variant="default" onClick={() => openModal("newWorkspace")}>
        + new workspace
      </Button>
    </>
  );
}

// -------------------------------------------------------- generic rename ----

function RenameGenericForm({ onDone }: { onDone: () => void }) {
  const target = useUiStore((s) => s.renameTarget);
  const renameRepo = useConsoleStore((s) => s.renameRepo);
  const renameKey = useConsoleStore((s) => s.renameKey);
  const [value, setValue] = useState(target?.current ?? "");

  useEffect(() => {
    setValue(target?.current ?? "");
  }, [target]);

  if (!target) return null;

  return (
    <>
      <Label>// new name for this {target.kind}</Label>
      <TextInput
        value={value}
        styles={inputStyles}
        onChange={(e) => setValue(e.currentTarget.value)}
      />
      <Group gap={10} mt={22} wrap="nowrap">
        <Button
          style={{ flex: 1 }}
          fw={600}
          disabled={value.trim().length === 0}
          onClick={() => {
            const run = target.kind === "repo" ? renameRepo : renameKey;
            void run(target.id, value.trim()).then(() => {
              notifications.show({ message: `renamed to ${value.trim()}` });
              onDone();
            });
          }}
        >
          ▸ rename
        </Button>
        <Button variant="default" onClick={onDone}>
          cancel
        </Button>
      </Group>
    </>
  );
}
